import { useEffect, useState } from 'react'
import { useAction } from 'convex/react'
import { api } from '../../../../convex/_generated/api'
import { Loader2, RefreshCw, CreditCard, Receipt, ExternalLink } from 'lucide-react'
import { useAuth } from '@/contexts/auth-context'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

type Settled<T> = T | { error: string }

type Transaction = {
  id?: string
  amount?: number
  currency_code?: string
  merchant_name?: string
  card_holder?: { first_name?: string; last_name?: string; user_id?: string; department_name?: string }
  user_transaction_time?: string
  state?: string
  sk_category_name?: string
  merchant_category_code_description?: string
  memo?: string
}

type RampCard = {
  id?: string
  display_name?: string
  last_four?: string
  state?: string
  cardholder_name?: string
  is_physical?: boolean
  expiration?: string
  spending_restrictions?: { amount?: number; interval?: string; transaction_amount_limit?: number | null; suspended?: boolean }
}

type Dashboard = {
  transactions: Settled<{ data: Transaction[]; page?: { next?: string } }>
  cards: Settled<{ data: RampCard[]; page?: { next?: string } }>
}

function isError<T>(x: Settled<T>): x is { error: string } {
  return typeof x === 'object' && x !== null && 'error' in (x as Record<string, unknown>)
}

function formatDollars(amount: number | undefined | null, currency = 'USD'): string {
  if (amount === undefined || amount === null) return '—'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount)
}

function formatDate(iso: string | undefined): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function ErrorCard({ error }: { error: string }) {
  const isMissingEnv = error.includes('RAMP_CLIENT_ID')
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm">
      <div className="font-medium text-amber-900">Couldn't load Ramp data</div>
      <pre className="mt-2 whitespace-pre-wrap break-words font-mono text-[11px] text-amber-800">{error}</pre>
      {isMissingEnv ? (
        <div className="mt-3 rounded border border-amber-300 bg-white p-3 text-[12px] text-amber-900">
          Set the credentials in Convex prod:
          <pre className="mt-1 font-mono text-[11px] text-amber-950">
            npx convex env set RAMP_CLIENT_ID &lt;client_id&gt; --prod{'\n'}
            npx convex env set RAMP_CLIENT_SECRET &lt;client_secret&gt; --prod
          </pre>
        </div>
      ) : null}
    </div>
  )
}

function Section({ icon: Icon, title, children, count }: { icon: typeof CreditCard; title: string; count?: number; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <div className="flex items-baseline justify-between">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-neutral-900">
          <Icon className="h-4 w-4 text-neutral-500" strokeWidth={2} />
          {title}
          {count !== undefined ? (
            <span className="text-[11px] font-normal text-neutral-400">({count})</span>
          ) : null}
        </h2>
      </div>
      {children}
    </section>
  )
}

export default function AdminRampPage() {
  const { user, isAuthenticated } = useAuth()
  const isAdmin = isAuthenticated && user?.isAdmin

  const [data, setData] = useState<Dashboard | null>(null)
  const [loading, setLoading] = useState(false)
  const [topError, setTopError] = useState<string | null>(null)

  const fetchDashboard = useAction(api.ramp.getDashboard)

  const load = async () => {
    setLoading(true)
    setTopError(null)
    try {
      const res = (await fetchDashboard({})) as Dashboard
      setData(res)
    } catch (e) {
      setTopError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!isAdmin) return
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin])

  if (!isAdmin) {
    return <div className="p-6 text-sm text-neutral-500">Admin access required</div>
  }

  return (
    <div className="space-y-6 pb-12">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold text-neutral-900">Ramp</h1>
          <p className="mt-0.5 text-sm text-neutral-500">
            Live data from{' '}
            <a
              className="underline decoration-dotted underline-offset-2 hover:text-neutral-700"
              href="https://app.ramp.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              app.ramp.com
              <ExternalLink className="ml-0.5 inline h-3 w-3" />
            </a>{' '}
            via the developer API.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
          <RefreshCw className={cn('mr-1.5 h-3.5 w-3.5', loading && 'animate-spin')} />
          Refresh
        </Button>
      </div>

      {topError ? <ErrorCard error={topError} /> : null}

      {!data && loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-5 w-5 animate-spin text-neutral-300" />
        </div>
      ) : null}

      {data ? (
        <>
          <Section
            icon={Receipt}
            title="Transactions"
            count={!isError(data.transactions) ? data.transactions.data.length : undefined}
          >
            {isError(data.transactions) ? (
              <ErrorCard error={data.transactions.error} />
            ) : (
              <TransactionsTable transactions={data.transactions.data} />
            )}
          </Section>

          <Section
            icon={CreditCard}
            title="Cards"
            count={!isError(data.cards) ? data.cards.data.length : undefined}
          >
            {isError(data.cards) ? (
              <ErrorCard error={data.cards.error} />
            ) : (
              <CardsTable cards={data.cards.data} />
            )}
          </Section>
        </>
      ) : null}
    </div>
  )
}

function TransactionsTable({ transactions }: { transactions: Transaction[] }) {
  if (transactions.length === 0) {
    return <Empty label="No transactions in the most recent page." />
  }
  return (
    <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white">
      <table className="w-full text-sm">
        <thead className="bg-neutral-50 text-left text-[11px] uppercase tracking-wide text-neutral-400">
          <tr>
            <Th>Date</Th>
            <Th>Merchant</Th>
            <Th>Cardholder</Th>
            <Th>Category</Th>
            <Th align="right">Amount</Th>
            <Th>State</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-100">
          {transactions.map((t, i) => (
            <tr key={t.id ?? i} className="hover:bg-neutral-50">
              <Td className="text-neutral-500">{formatDate(t.user_transaction_time)}</Td>
              <Td className="font-medium text-neutral-900">{t.merchant_name ?? '—'}</Td>
              <Td className="text-neutral-700">
                {t.card_holder
                  ? `${t.card_holder.first_name ?? ''} ${t.card_holder.last_name ?? ''}`.trim() || '—'
                  : '—'}
              </Td>
              <Td className="text-neutral-600">{t.sk_category_name ?? '—'}</Td>
              <Td align="right" className="font-mono tabular-nums text-neutral-900">
                {formatDollars(t.amount, t.currency_code)}
              </Td>
              <Td>
                <StatePill value={t.state} />
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function CardsTable({ cards }: { cards: RampCard[] }) {
  if (cards.length === 0) return <Empty label="No cards." />
  return (
    <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white">
      <table className="w-full text-sm">
        <thead className="bg-neutral-50 text-left text-[11px] uppercase tracking-wide text-neutral-400">
          <tr>
            <Th>Name</Th>
            <Th>Cardholder</Th>
            <Th>Last 4</Th>
            <Th>Type</Th>
            <Th>State</Th>
            <Th align="right">Limit</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-100">
          {cards.map((c, i) => (
            <tr key={c.id ?? i} className="hover:bg-neutral-50">
              <Td className="font-medium text-neutral-900">{c.display_name ?? '—'}</Td>
              <Td className="text-neutral-700">{c.cardholder_name ?? '—'}</Td>
              <Td className="font-mono text-neutral-600">{c.last_four ? `•••• ${c.last_four}` : '—'}</Td>
              <Td className="text-neutral-600">{c.is_physical ? 'physical' : 'virtual'}</Td>
              <Td>
                <StatePill value={c.state} />
              </Td>
              <Td align="right" className="font-mono tabular-nums text-neutral-700">
                {c.spending_restrictions?.amount !== undefined
                  ? `${formatDollars(c.spending_restrictions.amount)} / ${(c.spending_restrictions.interval ?? '').toLowerCase() || '—'}`
                  : '—'}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function StatePill({ value }: { value?: string }) {
  if (!value) return <span className="text-neutral-400">—</span>
  const v = value.toLowerCase()
  const tone =
    v.includes('clear') || v.includes('active')
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : v.includes('pending')
        ? 'bg-amber-50 text-amber-700 border-amber-200'
        : v.includes('decline') || v.includes('suspend') || v.includes('terminat') || v.includes('inactive')
          ? 'bg-rose-50 text-rose-700 border-rose-200'
          : 'bg-neutral-50 text-neutral-600 border-neutral-200'
  return (
    <span className={cn('rounded border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider', tone)}>
      {v.replace(/_/g, ' ')}
    </span>
  )
}

function Th({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return <th className={cn('px-3 py-2 font-medium', align === 'right' && 'text-right')}>{children}</th>
}

function Td({ children, align = 'left', className }: { children: React.ReactNode; align?: 'left' | 'right'; className?: string }) {
  return <td className={cn('px-3 py-2', align === 'right' && 'text-right', className)}>{children}</td>
}

function Empty({ label }: { label: string }) {
  return (
    <div className="rounded-lg border border-dashed border-neutral-200 bg-neutral-50/40 px-4 py-8 text-center text-sm text-neutral-400">
      {label}
    </div>
  )
}
